import * as THREE from 'three';
import GUI from 'lil-gui';

const props = {
  canvasSel: '.js-canvasWave', // クラスセレクタに変更
  lineCount: 40,
  lineSpacing: 0.035,
  // 波形パラメータを追加
  waveParams: {
    xWave1Amplitude: 0.1,
    xWave1Frequency: 0.3,
    xWave1Speed: 1.6,
    xWave2Amplitude: 0.2,
    xWave2Frequency: 0.6,
    xWave2Speed: 2,
    //
    zWave1Amplitude: 0.4,
    zWave1Frequency: 9,
    zWave1Speed: 2,
    zWave2Amplitude: 0.4,
    zWave2Frequency: 9,
    zWave2Speed: 3,
    //
    yWave1Amplitude: 0.2,
    yWave1Frequency: 8,
    yWave1Speed: 3,
    yWave2Amplitude: 0.2,
    yWave2Frequency: 10,
    yWave2Speed: 2.5,
  },
  cameraParams: {
    x: 0.7,
    y: 1,
    z: 1.1,
  },
} as const;

// statesの型定義を修正
type State = {
  canvasEl: HTMLCanvasElement;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer | null;
  lineGroup: THREE.Group | null;
  animationFrameId: number | null;
  resizeHandler: ((this: Window, ev: UIEvent) => void) | null;
  isInView: boolean; // 追加：要素が表示されているかのフラグ
  observer: IntersectionObserver | null; // 追加：IntersectionObserver インスタンス
};

const states: State[] = [];

// guiStateの型定義を修正
const guiState: {
  gui: GUI | undefined;
  guiFrameEl: HTMLElement | null;
  enabled: boolean;
} = {
  gui: undefined,
  guiFrameEl: null,
  enabled: false,
};

const vertexShader = `
  varying float vAlpha;
  varying float vX;
  uniform float time;
  uniform float lineIndex;
  uniform float lineCount;
  uniform float lineSpacing;

  // 波形パラメータのuniforms
  uniform float xWave1Amplitude;
  uniform float xWave1Frequency;
  uniform float xWave1Speed;
  uniform float xWave2Amplitude;
  uniform float xWave2Frequency;
  uniform float xWave2Speed;
  uniform float zWave1Amplitude;
  uniform float zWave1Frequency;
  uniform float zWave1Speed;
  uniform float zWave2Amplitude;
  uniform float zWave2Frequency;
  uniform float zWave2Speed;
  uniform float yWave1Amplitude;
  uniform float yWave1Frequency;
  uniform float yWave1Speed;
  uniform float yWave2Amplitude;
  uniform float yWave2Frequency;
  uniform float yWave2Speed;

  void main() {
    vec3 pos = position;

    float lineOffset = (lineIndex - lineCount * 0.5) / lineCount;
    float spreadAmount = lineOffset * 0.4;

    // 端点での補間係数を計算（0が端点、1が中央部）
    float edgeFactor = smoothstep(-96.0, -90.0, pos.x) * smoothstep(96.0, 90.0, -pos.x);

    // 位相差
    float phaseOffset = lineIndex * 0.2;
    float timeFactor = time + phaseOffset;

    // 波形の計算
    float waveX1 = sin(pos.x * xWave1Frequency + time * xWave1Speed) * xWave1Amplitude;
    float waveX2 = sin(pos.x * xWave2Frequency + time * xWave2Speed) * xWave2Amplitude;
    float finalWaveX = (waveX1 + waveX2) * edgeFactor;  // エッジで0に

    float waveZ1 = cos(pos.x * zWave1Frequency + time * zWave1Speed) * zWave1Amplitude;
    float waveZ2 = cos(pos.x * zWave2Frequency + time * zWave2Speed) * zWave2Amplitude;
    float finalWaveZ = (waveZ1 + waveZ2) * edgeFactor;  // エッジで0に

    // Y軸方向の波形
    float waveY1 = sin(pos.x * yWave1Frequency + time * yWave1Speed) * yWave1Amplitude;
    float waveY2 = sin(pos.x * yWave2Frequency + time * yWave2Speed) * yWave2Amplitude;
    float finalWaveY = (waveY1 + waveY2) * edgeFactor;  // エッジで0に

    // 波形の変調
    finalWaveX *= sin(timeFactor * 0.3) * 0.5 + 0.5;
    finalWaveY *= sin(timeFactor * 0.4) * 0.5 + 0.5;
    finalWaveZ *= cos(timeFactor * 0.3) * 0.5 + 0.5;

    // 位置の更新
    pos.x += finalWaveX * 0.3;
    pos.y = finalWaveY;
    pos.z += finalWaveZ * 0.3 + spreadAmount;

    // エッジのフェード処理
    float edgeFade = smoothstep(-1.0, -0.9, position.x) * smoothstep(1.0, 0.9, position.x);
    float depthAlpha = 1.0 - abs(lineOffset) * 0.5;
    vAlpha = edgeFade * depthAlpha * 0.9;

    vX = pos.x;

    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const fragmentShader = `
  varying float vAlpha;
  void main() {
    gl_FragColor = vec4(1.0, 1.0, 1.0, vAlpha * 0.8);
  }
`;

// const fragmentShader = `
//   varying float vAlpha;
//   varying float vX;
//   uniform float time;

//   void main() {
//     // 青系グラデーション
//     // ベース色を滑らかに時間で動かす
//     float t = sin(vX * 2.0 + time * 0.5) * 0.5 + 0.5;

//     // グラデーションの3色（濃い青 → 青 → 水色）
//     vec3 darkBlue = vec3(0.0, 0.1, 0.4);
//     vec3 blue     = vec3(0.0, 0.3, 0.8);
//     vec3 lightCyan = vec3(0.4, 0.8, 1.0);

//     // 2段階で滑らかに補間
//     vec3 midColor = mix(darkBlue, blue, t);
//     vec3 finalColor = mix(midColor, lightCyan, t * 0.8 + 0.2);

//     gl_FragColor = vec4(finalColor, vAlpha);
//   }
// `;

export const waveLine = {
  init() {
    const canvasEls = document.querySelectorAll<HTMLCanvasElement>(props.canvasSel);
    if (!canvasEls.length) return;

    // 各canvasに対してインスタンスを作成
    canvasEls.forEach((canvasEl) => {
      const state: State = {
        // 型アノテーションを追加
        canvasEl,
        scene: new THREE.Scene(),
        camera: new THREE.PerspectiveCamera(65, 1, 0.1, 10000),
        renderer: null,
        lineGroup: null,
        animationFrameId: null,
        resizeHandler: null, // 初期値をnullに
        isInView: false, // 追加
        observer: null, // 追加
      };

      // Scene setup
      state.camera.position.x = props.cameraParams.x;
      state.camera.position.y = props.cameraParams.y;
      state.camera.position.z = props.cameraParams.z;
      state.camera.lookAt(0, 0, 0);

      state.renderer = new THREE.WebGLRenderer({
        canvas: state.canvasEl,
        antialias: true,
        alpha: true,
      });
      state.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));

      // Ribbons
      state.lineGroup = new THREE.Group();
      this.setMesh(state);

      state.scene.add(state.lineGroup);

      // resize handlerの設定
      state.resizeHandler = () => this.onResize(state);
      window.addEventListener('resize', state.resizeHandler);
      this.onResize(state);

      // IntersectionObserver の設定を追加
      this.setupIntersectionObserver(state);

      states.push(state);
    });

    // GUIは1つだけ作成
    if (guiState.enabled && !guiState.gui) {
      this.setGUI();
    }
  },

  setMesh(state: (typeof states)[0]) {
    if (!state.lineGroup) return;

    const { lineCount, lineSpacing } = props;

    for (let i = 0; i < lineCount; i++) {
      const points: THREE.Vector3[] = [];
      const segments = 128;
      for (let j = 0; j <= segments; j++) {
        const x = (j / segments) * 2 - 1;
        points.push(new THREE.Vector3(x, 0, 0));
      }

      const geometry = new THREE.BufferGeometry().setFromPoints(points);
      const material = new THREE.ShaderMaterial({
        uniforms: {
          time: { value: 1.0 },
          lineIndex: { value: i },
          lineCount: { value: lineCount },
          lineSpacing: { value: lineSpacing },
          // 波形パラメータのuniforms
          xWave1Amplitude: { value: props.waveParams.xWave1Amplitude },
          xWave1Frequency: { value: props.waveParams.xWave1Frequency },
          xWave1Speed: { value: props.waveParams.xWave1Speed },
          xWave2Amplitude: { value: props.waveParams.xWave2Amplitude },
          xWave2Frequency: { value: props.waveParams.xWave2Frequency },
          xWave2Speed: { value: props.waveParams.xWave2Speed },
          zWave1Amplitude: { value: props.waveParams.zWave1Amplitude },
          zWave1Frequency: { value: props.waveParams.zWave1Frequency },
          zWave1Speed: { value: props.waveParams.zWave1Speed },
          zWave2Amplitude: { value: props.waveParams.zWave2Amplitude },
          zWave2Frequency: { value: props.waveParams.zWave2Frequency },
          zWave2Speed: { value: props.waveParams.zWave2Speed },
          yWave1Amplitude: { value: props.waveParams.yWave1Amplitude },
          yWave1Frequency: { value: props.waveParams.yWave1Frequency },
          yWave1Speed: { value: props.waveParams.yWave1Speed },
          yWave2Amplitude: { value: props.waveParams.yWave2Amplitude },
          yWave2Frequency: { value: props.waveParams.yWave2Frequency },
          yWave2Speed: { value: props.waveParams.yWave2Speed },
        },
        vertexShader,
        fragmentShader,
        transparent: true,
        depthTest: false,
      });

      const line = new THREE.Line(geometry, material);
      state.lineGroup.add(line);
    }
  },

  setGUI() {
    guiState.gui = new GUI();

    // Line CountとSpacingのコントロールを修正
    guiState.gui
      .add(props, 'lineCount', 10, 80, 1)
      .name('Line Count')
      .onChange(() => {
        // リボングループをクリア
        states.forEach((state) => {
          if (state.lineGroup) {
            state.lineGroup.clear();
            // メッシュを再生成
            this.setMesh(state);
          }
        });
      });

    guiState.gui
      .add(props, 'lineSpacing', 0.01, 0.1, 0.005)
      .name('Spacing')
      .onChange(() => {
        // リボングループをクリア
        states.forEach((state) => {
          if (state.lineGroup) {
            state.lineGroup.clear();
            // メッシュを再生成
            this.setMesh(state);
          }
        });
      });

    // X波形のフォルダ
    const xWaveFolder = guiState.gui.addFolder('X Wave Parameters');
    xWaveFolder.add(props.waveParams, 'xWave1Amplitude', 0, 10, 0.1).name('Wave1 Amplitude').onChange(this.updateWaveParams);
    xWaveFolder.add(props.waveParams, 'xWave1Frequency', 0, 20, 0.1).name('Wave1 Frequency').onChange(this.updateWaveParams);
    xWaveFolder.add(props.waveParams, 'xWave1Speed', 0, 3, 0.1).name('Wave1 Speed').onChange(this.updateWaveParams);
    xWaveFolder.add(props.waveParams, 'xWave2Amplitude', 0, 10, 0.1).name('Wave2 Amplitude').onChange(this.updateWaveParams);
    xWaveFolder.add(props.waveParams, 'xWave2Frequency', 0, 20, 0.1).name('Wave2 Frequency').onChange(this.updateWaveParams);
    xWaveFolder.add(props.waveParams, 'xWave2Speed', 0, 3, 0.1).name('Wave2 Speed').onChange(this.updateWaveParams);

    // Z波形のフォルダ
    const zWaveFolder = guiState.gui.addFolder('Z Wave Parameters');
    zWaveFolder.add(props.waveParams, 'zWave1Amplitude', 0, 10, 0.1).name('Wave1 Amplitude').onChange(this.updateWaveParams);
    zWaveFolder.add(props.waveParams, 'zWave1Frequency', 0, 20, 0.1).name('Wave1 Frequency').onChange(this.updateWaveParams);
    zWaveFolder.add(props.waveParams, 'zWave1Speed', 0, 3, 0.1).name('Wave1 Speed').onChange(this.updateWaveParams);
    zWaveFolder.add(props.waveParams, 'zWave2Amplitude', 0, 10, 0.1).name('Wave2 Amplitude').onChange(this.updateWaveParams);
    zWaveFolder.add(props.waveParams, 'zWave2Frequency', 0, 20, 0.1).name('Wave2 Frequency').onChange(this.updateWaveParams);
    zWaveFolder.add(props.waveParams, 'zWave2Speed', 0, 3, 0.1).name('Wave2 Speed').onChange(this.updateWaveParams);

    // Y波形のフォルダ
    const yWaveFolder = guiState.gui.addFolder('Y Wave Parameters');
    yWaveFolder.add(props.waveParams, 'yWave1Amplitude', 0, 10, 0.1).name('Wave1 Amplitude').onChange(this.updateWaveParams);
    yWaveFolder.add(props.waveParams, 'yWave1Frequency', 0, 20, 0.1).name('Wave1 Frequency').onChange(this.updateWaveParams);
    yWaveFolder.add(props.waveParams, 'yWave1Speed', 0, 3, 0.1).name('Wave1 Speed').onChange(this.updateWaveParams);
    yWaveFolder.add(props.waveParams, 'yWave2Amplitude', 0, 10, 0.1).name('Wave2 Amplitude').onChange(this.updateWaveParams);
    yWaveFolder.add(props.waveParams, 'yWave2Frequency', 0, 20, 0.1).name('Wave2 Frequency').onChange(this.updateWaveParams);
    yWaveFolder.add(props.waveParams, 'yWave2Speed', 0, 3, 0.1).name('Wave2 Speed').onChange(this.updateWaveParams);

    // カメラコントロール
    const cameraFolder = guiState.gui.addFolder('Camera');
    if (states[0] && states[0].camera) {
      cameraFolder
        .add(states[0].camera.position, 'x', -5, 5, 0.01)
        .name('X')
        .onChange(() => {
          states.forEach((state) => {
            if (state.camera) {
              state.camera.position.x = states[0].camera!.position.x;
              state.camera.updateProjectionMatrix();
            }
          });
        });
      cameraFolder
        .add(states[0].camera.position, 'y', -5, 5, 0.01)
        .name('Y')
        .onChange(() => {
          states.forEach((state) => {
            if (state.camera) {
              state.camera.position.y = states[0].camera!.position.y;
              state.camera.updateProjectionMatrix();
            }
          });
        });
      cameraFolder
        .add(states[0].camera.position, 'z', 0.1, 10, 0.01)
        .name('Z')
        .onChange(() => {
          states.forEach((state) => {
            if (state.camera) {
              state.camera.position.z = states[0].camera!.position.z;
              state.camera.updateProjectionMatrix();
            }
          });
        });
    }
  },

  // パラメータ更新用のメソッドを追加
  updateWaveParams() {
    states.forEach((state) => {
      if (!state.lineGroup) return;

      state.lineGroup.children.forEach((line) => {
        const material = (line as THREE.Line).material as THREE.ShaderMaterial;
        Object.entries(props.waveParams).forEach(([key, value]) => {
          if (material.uniforms[key]) {
            material.uniforms[key].value = value;
          }
        });
      });
    });
  },

  onResize(state: (typeof states)[0]) {
    if (!state.renderer || !state.camera || !state.lineGroup || !state.canvasEl) return;

    const w = state.canvasEl.clientWidth;
    const h = state.canvasEl.clientHeight;

    // 内部解像度更新
    state.renderer.setSize(w, h, false); // falseならCSSサイズ維持
    state.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));

    // カメラ更新
    state.camera.aspect = w / h;
    state.camera.updateProjectionMatrix();

    // （任意）見た目スケール調整
    if (state.lineGroup) {
      state.lineGroup.scale.set(state.camera.aspect * 4, 0.75, 1);
    }
  },

  // IntersectionObserver のセットアップメソッドを追加
  setupIntersectionObserver(state: State) {
    if (!state.canvasEl) return;

    state.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          state.isInView = entry.isIntersecting;

          if (entry.isIntersecting) {
            // console.log('🟢 アニメーション開始:', state.canvasEl.dataset.name || '無名canvas');
            if (state.animationFrameId === null) {
              this.animateInstance(state);
            }
          } else {
            // console.log('🔴 アニメーション停止:', state.canvasEl.dataset.name || '無名canvas');
            if (state.animationFrameId !== null) {
              cancelAnimationFrame(state.animationFrameId);
              state.animationFrameId = null;
            }
          }
        });
      },
      {
        threshold: 0,
      },
    );

    state.observer.observe(state.canvasEl);
  },

  // 個別のインスタンスのアニメーションを処理するメソッドを追加
  animateInstance(state: State) {
    if (!state.lineGroup || !state.renderer || !state.scene || !state.camera) return;

    if (state.isInView) {
      // // フレームレートをモニタリング（開発時のみ）
      // if (process.env.NODE_ENV === 'development') {
      //   console.log('⚡️ Wave Line フレーム更新');
      // }

      // --- 時間更新 ---
      state.lineGroup.children.forEach((line) => {
        const material = (line as THREE.Line).material as THREE.ShaderMaterial;
        material.uniforms.time.value += 0.015;
      });

      // --- カメラを常に中心に向ける ---
      state.camera.lookAt(0, 0, 0);

      state.renderer.render(state.scene, state.camera);
      state.animationFrameId = requestAnimationFrame(() => this.animateInstance(state));
    }
  },

  reset() {
    // 全インスタンスをクリーンアップ
    states.forEach((state) => {
      // IntersectionObserver の解除を追加
      if (state.observer) {
        state.observer.disconnect();
        state.observer = null;
      }

      // 停止
      if (state.animationFrameId !== null) {
        cancelAnimationFrame(state.animationFrameId);
        state.animationFrameId = null;
      }

      // resizeイベント削除
      if (state.resizeHandler) {
        window.removeEventListener('resize', state.resizeHandler);
        state.resizeHandler = null; // nullからundefinedに変更
      }

      // ribbon破棄
      if (state.lineGroup) {
        state.lineGroup.children.forEach((line) => {
          const mesh = line as THREE.Line;
          mesh.geometry.dispose();
          (mesh.material as THREE.ShaderMaterial).dispose();
        });
        state.lineGroup.clear();
        state.lineGroup = null;
      }

      // renderer破棄
      if (state.renderer) {
        state.renderer.dispose();
        state.renderer = null;
      }

      state.scene.clear();
    });
    states.length = 0; // 配列をクリア

    // GUI破棄
    guiState.gui?.destroy();
    guiState.gui = undefined;
    guiState.guiFrameEl = null;
  },
};
